/**
 * `link` → **Settings** (spec M2 tier-2 §5.1, card P-123).
 *
 * `link`'s captured `Settings` submenu (`editors/link/settings-menu.json`) has no *Customize*
 * row, but the type is not setting-free: `linkSettingsSchema` carries the two keys the API
 * documents `[09§A.7 link]` — `defaultText`, the display text a cell falls back to when the user
 * types a url and no text, and `disableAutoTitle`, monday's switch for fetching the page title.
 * (`settings` is `{}` on every link column of this account `[cvs§2 link]`, so both are at their
 * defaults there; that is why the dialog was never photographed.)
 *
 * The auto-title fetch itself is not implemented — this product never calls out to a url — so the
 * switch stores monday's key and the cell keeps the text the user typed. q522.
 */
import type { LinkSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SettingsCheckbox,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

export function LinkSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<LinkSettings>) {
  const [draft, patch] = useSettingsDraft<LinkSettings>(settings);
  return (
    <SettingsForm
      title="Link column settings"
      testId="link-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsField
        label="Default display text"
        testId="link-default-text"
        value={draft.defaultText ?? ''}
        placeholder="The url itself"
        onChange={(next) => patch({ defaultText: next })}
      />
      <SettingsCheckbox
        label="Do not fetch the page title"
        hint="A pasted url keeps the text you typed instead of the linked page's title."
        testId="link-disable-auto-title"
        checked={draft.disableAutoTitle ?? false}
        onChange={(next) => patch({ disableAutoTitle: next })}
      />
      <HideSummaryField
        testId="link-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
